import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Footer from "../components/Footer";
import {
  EmailAuthProvider,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { Helmet } from "react-helmet";
import { db } from "../firebase";
import { addDoc, collection } from "firebase/firestore";
import AutoLeads from "../components/AutoLeads";
import WebhookUrl from "../components/WebhookUrl";

function Account() {
  const auth = getAuth();
  const navigate = useNavigate();
  const apiKey = process.env.REACT_APP_API_KEY;
  const [uid, setUid] = useState(null);
  const [user, setUser] = useState({
    full_name: "",
    email: "",
    phone: "",
    userName: "",
    autoPurchaseSettings: "",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        setUid(null);
        navigate("/sign-in");
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!uid) return;
      try {
        const response = await fetch(
          `https://api.v1.spotyourleads.com/user/${uid}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": apiKey,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const userData = await response.json();

        setUser(userData); // Update your state with user data
      } catch (error) {
        console.error("Error fetching user data:", error.message);
      }
    };

    fetchUserData();
  }, [uid, apiKey]);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };
  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    if (!uid) {
      console.error("User ID not found!");
      return;
    }
    try {
      if (!selectedFile) {
        Swal.fire({
          icon: "warning",
          title: "No file selected!",
          showConfirmButton: true,
          timer: 1500,
          customClass: {
            confirmButton: "my-confirm-button",
          },
        });
        return;
      }

      const formData = new FormData();
      formData.append("userId", uid);
      formData.append("photo", selectedFile);
      const response = await fetch(
        "https://api.v1.spotyourleads.com/user/upload-profile-photo",
        {
          method: "POST",
          headers: {
            "x-api-key": apiKey,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      const responseData = await response.json();
      // Show success message

      Swal.fire({
        icon: "success",
        title: "Updated Successfully",
        showConfirmButton: true,
        timer: 1500,
        customClass: {
          confirmButton: "my-confirm-button",
        },
      });
    } catch (error) {
      console.error("Error uploading file and updating document:", error);

      // Show error message
      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        showConfirmButton: true,
        timer: 1500,
        customClass: {
          confirmButton: "my-confirm-button",
        },
      });
    }
  };
  const onPasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwords.newPassword != passwords.confirmNewPassword) {
      Swal.fire({
        icon: "error",
        title: `Passwords do not match!`,
        showConfirmButton: true,
        timer: 1500,
        customClass: {
          confirmButton: "my-confirm-button",
        },
      });
      return;
    }

    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user.email,
        passwords.currentPassword
      );

      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwords.newPassword);
      const mailOptions = {
        to: user.email,
        message: {
          subject: "Your Password Has Been Changed",
          html: `<p>Hello,</p><p>We wanted to inform you that your password has been successfully changed. If you did not request this change, please contact us immediately.</p><p>Best regards,<br>The Spot Your Leads Team</p>`,
        },
      };

      Swal.fire({
        icon: "success",
        title: `Password updated successfully!`,
        showConfirmButton: true,
        timer: 1500,
        customClass: {
          confirmButton: "my-confirm-button",
        },
      })
        .then(await addDoc(collection(db, "mail"), mailOptions))
        .then(
          setPasswords({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          })
        );
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        Swal.fire({
          icon: "error",
          title: `Incorrect current password!`,
          showConfirmButton: true,
          timer: 1500,
          customClass: {
            confirmButton: "my-confirm-button",
          },
        });
      } else if (error.code === "auth/requires-recent-login") {
        Swal.fire({
          icon: "error",
          title: `Please log in again to update your password.`,
          showConfirmButton: true,
          timer: 1500,
          customClass: {
            confirmButton: "my-confirm-button",
          },
        });
      } else {
        Swal.fire({
          icon: "error",
          title: error.message,
          showConfirmButton: true,
          timer: 1500,
          customClass: {
            confirmButton: "my-confirm-button",
          },
        });
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Spot Your Leads | Manage Your Real Estate Lead Profile </title>
      </Helmet>
      <section className="homeFourth formSection accountEdit inquiry">
        <h2>Account Settings</h2>
        <Container>
          <Row>
            <Col lg={12} md={12} sm={12}>
              <div className="formDiv">
                <form onSubmit={handleProfileUpdate}>
                  <div className="row justify-content-center px-5">
                    <div className="col-md-3">
                      <div>
                        <input
                          type="file"
                          id="fileInput"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                        <label htmlFor="fileInput">
                          {selectedFile ? (
                            <div className="imageFile upload">
                              <img
                                src={URL.createObjectURL(selectedFile)}
                                alt="upload-profile"
                              />
                            </div>
                          ) : (
                            <div className="imageFile imggg">
                              <img
                                src={user.photoUrl || "images/account.svg"}
                                alt="upload-profile"
                              />
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      {selectedFile ? (
                        <button type="submit">Save</button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            document.getElementById("fileInput").click()
                          }
                        >
                          Upload Photo
                        </button>
                      )}
                    </div>
                  </div>
                </form>
                <form
                // onSubmit={handlePasswordChange}
                >
                  <div className="row justify-content-between mt-1">
                    <div className="col-md-5">
                      <label>User Name</label>
                      <input
                        className="form-control"
                        name="currentPassword"
                        value={user.name}
                        disabled
                        // onChange={onPasswordChange}
                        type="text"
                        placeholder="User Name"
                      />
                    </div>
                    {/* <div className='col-md-5'>
                                            <input
                                                className="form-control"
                                                name="newPassword"
                                                value={passwords.newPassword}
                                                // onChange={onPasswordChange}
                                                type='password'
                                                placeholder=" Password"
                                            />
                                        </div> */}
                  </div>
                  {/* <button type="submit">Change User Name</button> */}
                </form>
                <form>
                  <div className="row justify-content-between mt-1">
                    <div className="col-md-5">
                      <label>Email</label>
                      <input
                        className="form-control"
                        name="currentPassword"
                        value={user.email}
                        disabled
                        // value={passwords.currentPassword}
                        // onChange={onPasswordChange}
                        type="email"
                        placeholder="Email"
                      />
                    </div>
                    {/* <div className='col-md-5'>
                                            <input
                                                className="form-control"
                                                name="newPassword"

                                                // value={passwords.newPassword}
                                                // onChange={onPasswordChange}
                                                type='password'
                                                placeholder=" Password"
                                            />
                                        </div> */}
                  </div>
                  {/* <button type="submit">Change Email</button> */}
                </form>
                <form onSubmit={handlePasswordChange}>
                  <div className="row justify-content-between mt-1">
                    <div className="col-md-5">
                      <label>Current Password</label>
                      <input
                        className="form-control"
                        name="currentPassword"
                        value={passwords.currentPassword}
                        onChange={onPasswordChange}
                        type="password"
                        placeholder="Current Password"
                      />
                    </div>
                    <div className="col-md-5">
                      <label>New Password</label>
                      <input
                        className="form-control"
                        name="newPassword"
                        value={passwords.newPassword}
                        onChange={onPasswordChange}
                        type="password"
                        placeholder="New Password"
                      />
                    </div>
                    <div className="col-md-5">
                      <label>Confirm New Password</label>
                      <input
                        className="form-control"
                        name="confirmNewPassword"
                        value={passwords.confirmNewPassword}
                        onChange={onPasswordChange}
                        type="password"
                        placeholder="Confirm New Password"
                      />
                    </div>
                  </div>
                  <button type="submit">Change Password</button>
                </form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <AutoLeads autoPurchaseSettings={user.autoPurchaseSettings} />
      <WebhookUrl webhookSettings={user.webhookSettings} />
      <Footer />
    </>
  );
}

export default Account;
